import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Save, Eye, Globe, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const GuestWebsiteBuilder = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    url_slug: '',
    couple_story: '',
    ceremony_details: '',
    reception_details: '',
    custom_message: '',
    theme_color: '#881337'
  });

  useEffect(() => {
    loadWebsite();
  }, [user.id]);

  const loadWebsite = async () => {
    try {
      const response = await axios.get(`${API}/guest-websites/user/${user.id}`);
      if (response.data) {
        setWebsite(response.data);
        setFormData({
          url_slug: response.data.url_slug,
          couple_story: response.data.couple_story || '',
          ceremony_details: response.data.ceremony_details || '',
          reception_details: response.data.reception_details || '',
          custom_message: response.data.custom_message || '',
          theme_color: response.data.theme_color || '#881337'
        });
      }
    } catch (error) {
      console.error('Error loading website:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/guest-websites`, formData, {
        headers: { 'user-id': user.id }
      });
      toast.success('Guest website created!');
      loadWebsite();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create website');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/guest-websites/${website.id}`, formData);
      toast.success('Website updated!');
      loadWebsite();
    } catch (error) {
      toast.error('Failed to update website');
    }
  };

  const handlePublish = async () => {
    try {
      await axios.put(`${API}/guest-websites/${website.id}`, { published: !website.published });
      toast.success(website.published ? 'Website unpublished' : 'Website published!');
      loadWebsite();
    } catch (error) {
      toast.error('Failed to publish website');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-xl border-b border-stone-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Button
            data-testid="back-to-dashboard-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#57534E] hover:text-[#881337]"
          >
            <ArrowLeft className="mr-2" size={18} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-serif font-light tracking-tight text-[#1C1917] mb-2">
            Guest Website Builder
          </h1>
          <p className="text-[#57534E]">Create a beautiful website to share with your guests</p>
        </div>

        <form onSubmit={website ? handleUpdate : handleCreate} className="space-y-8">
          {/* URL Slug */}
          {!website && (
            <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-white/50">
              <Label htmlFor="url_slug" className="text-[#1C1917] mb-4 block text-lg font-serif">Website URL</Label>
              <div className="flex items-center gap-2">
                <span className="text-[#57534E]">{window.location.origin}/wedding/</span>
                <Input
                  data-testid="url-slug-input"
                  id="url_slug"
                  value={formData.url_slug}
                  onChange={(e) => setFormData({ ...formData, url_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0 flex-1"
                  placeholder="your-names"
                  required
                />
              </div>
              <p className="text-xs text-[#A8A29E] mt-2">Choose a unique URL for your wedding website</p>
            </div>
          )}

          {website && (
            <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/50 flex items-center justify-between">
              <div>
                <p className="text-xs text-[#A8A29E] mb-1">Your Website URL</p>
                <p className="text-[#1C1917] font-medium">{window.location.origin}/wedding/{website.url_slug}</p>
              </div>
              <div className="flex gap-3">
                <Button
                  data-testid="view-website-btn"
                  type="button"
                  variant="outline"
                  onClick={() => window.open(`/wedding/${website.url_slug}`, '_blank')}
                  className="rounded-full"
                >
                  <Eye className="mr-2" size={18} />
                  Preview
                </Button>
                <Button
                  data-testid="publish-website-btn"
                  type="button"
                  onClick={handlePublish}
                  className={`rounded-full ${website.published ? 'bg-[#84A98C] hover:bg-[#84A98C]/90' : 'bg-[#881337] hover:bg-[#9F1239]'} text-white`}
                >
                  <Globe className="mr-2" size={18} />
                  {website.published ? 'Published' : 'Publish'}
                </Button>
              </div>
            </div>
          )}

          {/* Couple Story */}
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-white/50">
            <Label htmlFor="couple_story" className="text-[#1C1917] mb-4 block text-lg font-serif">Your Love Story</Label>
            <Textarea
              data-testid="couple-story-input"
              id="couple_story"
              value={formData.couple_story}
              onChange={(e) => setFormData({ ...formData, couple_story: e.target.value })}
              className="min-h-32 border border-stone-300 rounded-xl p-4"
              placeholder="Tell your story..."
            />
          </div>

          {/* Ceremony Details */}
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-white/50">
            <Label htmlFor="ceremony_details" className="text-[#1C1917] mb-4 block text-lg font-serif">Ceremony Details</Label>
            <Textarea
              data-testid="ceremony-details-input"
              id="ceremony_details"
              value={formData.ceremony_details}
              onChange={(e) => setFormData({ ...formData, ceremony_details: e.target.value })}
              className="min-h-24 border border-stone-300 rounded-xl p-4"
              placeholder="Date, time, location..."
            />
          </div>

          {/* Reception Details */}
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-white/50">
            <Label htmlFor="reception_details" className="text-[#1C1917] mb-4 block text-lg font-serif">Reception Details</Label>
            <Textarea
              data-testid="reception-details-input"
              id="reception_details"
              value={formData.reception_details}
              onChange={(e) => setFormData({ ...formData, reception_details: e.target.value })}
              className="min-h-24 border border-stone-300 rounded-xl p-4"
              placeholder="Date, time, location..."
            />
          </div>

          {/* Custom Message */}
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-white/50">
            <Label htmlFor="custom_message" className="text-[#1C1917] mb-4 block text-lg font-serif">Custom Message</Label>
            <Textarea
              data-testid="custom-message-input"
              id="custom_message"
              value={formData.custom_message}
              onChange={(e) => setFormData({ ...formData, custom_message: e.target.value })}
              className="min-h-24 border border-stone-300 rounded-xl p-4"
              placeholder="A special message for your guests..."
            />
          </div>

          {/* Theme Color */}
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-white/50">
            <Label htmlFor="theme_color" className="text-[#1C1917] mb-4 block text-lg font-serif">Theme Color</Label>
            <div className="flex gap-4 items-center">
              <input
                data-testid="theme-color-input"
                type="color"
                id="theme_color"
                value={formData.theme_color}
                onChange={(e) => setFormData({ ...formData, theme_color: e.target.value })}
                className="w-20 h-20 rounded-full cursor-pointer"
              />
              <div>
                <p className="text-[#1C1917] font-medium">{formData.theme_color}</p>
                <p className="text-xs text-[#A8A29E]">Choose your wedding color</p>
              </div>
            </div>
          </div>

          {/* Submit */}
          <Button
            data-testid="save-website-btn"
            type="submit"
            className="w-full rounded-full py-6 bg-[#881337] hover:bg-[#9F1239] text-white text-lg"
          >
            <Save className="mr-2" size={20} />
            {website ? 'Update Website' : 'Create Website'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default GuestWebsiteBuilder;